/**
 * ParticipantsPanel - Shows list of participants in the call
 * Displays name, mute status, video status for each participant
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    useRosterState,
    useAttendeeStatus
} from 'amazon-chime-sdk-component-library-react';
import './VideoCall.css';

// Signal strength is 0-1 from Chime's own attendee signalling. Below a third of full is
// where the audio starts arriving in pieces, and 0 means the attendee's connection is gone
// even though they are still listed - which is exactly the case people misread as "they are
// ignoring us" on a bridge.
const SIGNAL_WEAK = 0.34;

// Individual participant item
function ParticipantItem({ attendeeId, name, handRaiseOrder, isSpeaking }) {
    const { t } = useTranslation();
    const { muted, videoEnabled, signalStrength } = useAttendeeStatus(attendeeId);

    // Get initials from name
    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    return (
        <div className={`participant-item${isSpeaking ? ' is-speaking' : ''}`}>
            <div className="participant-avatar">
                {getInitials(name)}
                {handRaiseOrder >= 0 && (
                    <span className="participant-hand-badge">{'\u270B'}</span>
                )}
            </div>
            <div className="participant-info">
                <div className="participant-name-row">
                    <span className="participant-name">{name || t('aiChat.messages.unknown')}</span>
                    {isSpeaking && (
                        <span className="participant-speaking" title={t('aiChat.calls.speaking')}>
                            <span className="active-speaker-dot" aria-hidden="true" />
                            {t('aiChat.calls.speaking')}
                        </span>
                    )}
                    {handRaiseOrder >= 0 && (
                        <span className="participant-hand-order">#{handRaiseOrder + 1}</span>
                    )}
                </div>
                <div className="participant-status-icons">
                    {/* A weak or dead link, said plainly. Without it the person who has
                        dropped off the audio path looks exactly like the person who has
                        nothing to say. */}
                    {typeof signalStrength === 'number' && signalStrength <= 0 && (
                        <span className="status-icon signal-lost" title={t('aiChat.calls.signalLost')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="1" y1="1" x2="23" y2="23" />
                                <path d="M5 12.55a11 11 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.58 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0" />
                                <line x1="12" y1="20" x2="12.01" y2="20" />
                            </svg>
                        </span>
                    )}
                    {typeof signalStrength === 'number' && signalStrength > 0 && signalStrength < SIGNAL_WEAK && (
                        <span className="status-icon signal-weak" title={t('aiChat.calls.signalWeak')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                                <line x1="12" y1="20" x2="12.01" y2="20" />
                            </svg>
                        </span>
                    )}
                    {muted && (
                        <span className="status-icon muted" title={t('aiChat.calls.muted')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="1" y1="1" x2="23" y2="23" />
                                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" />
                                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" />
                            </svg>
                        </span>
                    )}
                    {!videoEnabled && (
                        <span className="status-icon video-off" title={t('aiChat.calls.cameraOffShort')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                            </svg>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}

function ParticipantsPanel({ isOpen, onClose, onInvite, sessionParticipants = [], raisedHands = [], activeSpeakerId = null }) {
    const { t } = useTranslation();
    const { roster } = useRosterState();

    // Get list of attendees from roster
    const attendees = Object.entries(roster).map(([attendeeId, attendee]) => ({
        attendeeId,
        externalUserId: attendee.externalUserId,
        name: attendee.name || attendee.externalUserId || t('aiChat.calls.participant')
    }));

    if (!isOpen) return null;

    return (
        <div className="participants-panel">
            <div className="participants-panel-header">
                <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    {t('aiChat.participants.title', { count: attendees.length })}
                </h3>
                <button className="panel-close-btn" onClick={onClose}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            </div>

            <div className="participants-list">
                {attendees.length === 0 ? (
                    <div className="no-participants">
                        <span>{t('aiChat.calls.noParticipantsYet')}</span>
                    </div>
                ) : (
                    attendees.map(({ attendeeId, externalUserId, name }) => (
                        <ParticipantItem
                            key={attendeeId}
                            attendeeId={attendeeId}
                            name={name}
                            handRaiseOrder={raisedHands.findIndex(h => String(h.userId) === String(externalUserId))}
                            isSpeaking={activeSpeakerId === attendeeId}
                        />
                    ))
                )}
            </div>

            {/* Invite button */}
            <div className="participants-panel-footer">
                <button className="invite-btn" onClick={onInvite}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <line x1="20" y1="8" x2="20" y2="14" />
                        <line x1="23" y1="11" x2="17" y2="11" />
                    </svg>
                    {t('aiChat.calls.inviteOthersButton')}
                </button>
            </div>
        </div>
    );
}

export default ParticipantsPanel;
